import React, { useState, useEffect } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import {
  VideoTutorial,
  getVideoTutorials,
  createVideoTutorial,
  updateVideoTutorial,
  deleteVideoTutorial,
  extractYouTubeId,
  getYouTubeThumbnail,
} from '../../services/videoTutorialService';

const AdminVideoTutorialManager: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [tutorials, setTutorials] = useState<VideoTutorial[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState<VideoTutorial | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    youtubeUrl: '',
    isActive: true,
    order: 0,
  });

  useEffect(() => {
    loadTutorials();
  }, []);

  const loadTutorials = async () => {
    try {
      setIsLoading(true);
      const data = await getVideoTutorials();
      setTutorials(data);
    } catch (error) {
      console.error('Error loading tutorials:', error);
      showError('Failed to load video tutorials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTutorial = async () => {
    try {
      if (!formData.title.trim()) {
        showError('Please enter a title');
        return;
      }

      const youtubeId = extractYouTubeId(formData.youtubeUrl);
      if (!youtubeId) {
        showError('Please enter a valid YouTube URL or video ID');
        return;
      }

      await createVideoTutorial({
        title: formData.title.trim(),
        youtubeId,
        thumbnail: getYouTubeThumbnail(youtubeId),
        isActive: formData.isActive,
        order: formData.order || tutorials.length,
      });

      showSuccess('Video tutorial added successfully');
      setShowAddModal(false);
      resetForm();
      await loadTutorials();
    } catch (error) {
      console.error('Error adding tutorial:', error);
      showError('Failed to add video tutorial');
    }
  };

  const handleUpdateTutorial = async () => {
    if (!editingTutorial) return;

    try {
      if (!formData.title.trim()) {
        showError('Please enter a title');
        return;
      }

      const youtubeId = extractYouTubeId(formData.youtubeUrl);
      if (!youtubeId) {
        showError('Please enter a valid YouTube URL or video ID');
        return;
      }

      await updateVideoTutorial(editingTutorial.id, {
        title: formData.title.trim(),
        youtubeId,
        thumbnail: getYouTubeThumbnail(youtubeId),
        isActive: formData.isActive,
        order: formData.order,
      });

      showSuccess('Video tutorial updated successfully');
      setShowAddModal(false);
      setEditingTutorial(null);
      resetForm();
      await loadTutorials();
    } catch (error) {
      console.error('Error updating tutorial:', error);
      showError('Failed to update video tutorial');
    }
  };

  const handleDeleteTutorial = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this video tutorial?')) {
      return;
    }

    try {
      await deleteVideoTutorial(id);
      showSuccess('Video tutorial deleted successfully');
      await loadTutorials();
    } catch (error) {
      console.error('Error deleting tutorial:', error);
      showError('Failed to delete video tutorial');
    }
  };

  const handleToggleActive = async (tutorial: VideoTutorial) => {
    try {
      await updateVideoTutorial(tutorial.id, {
        isActive: !tutorial.isActive,
      });
      showSuccess(`Video tutorial ${!tutorial.isActive ? 'activated' : 'deactivated'}`);
      await loadTutorials();
    } catch (error) {
      console.error('Error updating tutorial status:', error);
      showError('Failed to update video tutorial status');
    }
  };

  const openEditModal = (tutorial: VideoTutorial) => {
    setFormData({
      title: tutorial.title,
      youtubeUrl: `https://www.youtube.com/watch?v=${tutorial.youtubeId}`,
      isActive: tutorial.isActive !== false,
      order: tutorial.order || 0,
    });
    setEditingTutorial(tutorial);
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      youtubeUrl: '',
      isActive: true,
      order: 0,
    });
    setEditingTutorial(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Video Tutorial Manager</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Manage video tutorials for the discover page</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
          className="bg-black hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-200 text-white dark:text-black px-6 py-2 rounded-lg font-medium transition-colors duration-200"
        >
          Add Video Tutorial
        </button>
      </div>

      {/* Tutorials List */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-white mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading tutorials...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tutorials.map((tutorial) => (
            <div
              key={tutorial.id}
              className="bg-white dark:bg-neutral-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-neutral-600"
            >
              {/* Thumbnail */}
              <div className="relative h-48 bg-gray-200 dark:bg-neutral-700">
                {tutorial.thumbnail ? (
                  <img
                    src={tutorial.thumbnail}
                    alt={tutorial.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback to YouTube default thumbnail if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.src = `https://img.youtube.com/vi/${tutorial.youtubeId}/hqdefault.jpg`;
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-16 h-16 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      tutorial.isActive !== false
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                    }`}
                  >
                    {tutorial.isActive !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Tutorial Info */}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                  {tutorial.title}
                </h3>

                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
                  <span>Order: {tutorial.order || 0}</span>
                  <span>ID: {tutorial.youtubeId}</span>
                </div>

                {/* Actions */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => openEditModal(tutorial)}
                    className="flex-1 px-3 py-2 text-sm bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-neutral-600 transition-colors duration-200"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleActive(tutorial)}
                    className={`flex-1 px-3 py-2 text-sm rounded transition-colors duration-200 ${
                      tutorial.isActive !== false
                        ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-200 hover:bg-yellow-200 dark:hover:bg-yellow-800'
                        : 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800'
                    }`}
                  >
                    {tutorial.isActive !== false ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleDeleteTutorial(tutorial.id)}
                    className="px-3 py-2 text-sm bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded hover:bg-red-200 dark:hover:bg-red-800 transition-colors duration-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}

          {tutorials.length === 0 && (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">No video tutorials found. Add your first tutorial!</p>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {editingTutorial ? 'Edit Video Tutorial' : 'Add New Video Tutorial'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent bg-white dark:bg-neutral-700 text-gray-900 dark:text-white"
                  placeholder="Enter tutorial title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  YouTube URL or Video ID *
                </label>
                <input
                  type="text"
                  value={formData.youtubeUrl}
                  onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent bg-white dark:bg-neutral-700 text-gray-900 dark:text-white"
                  placeholder="https://www.youtube.com/watch?v=... or video ID"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  You can paste a full YouTube URL or just the video ID
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent bg-white dark:bg-neutral-700 text-gray-900 dark:text-white"
                  placeholder="0"
                  min="0"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                />
                <label htmlFor="isActive" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active (visible on discover page)
                </label>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-neutral-600 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={editingTutorial ? handleUpdateTutorial : handleAddTutorial}
                className="flex-1 px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors duration-200"
              >
                {editingTutorial ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVideoTutorialManager;

